<template>
  <div class="face-detection-container">
    <div :class="['face-search-container', { 'face-search-large': large }]">
      <!-- Form: Watchlist + Image + Search Button -->
      <div v-if="showForm" class="form-section">
        <!-- Watchlist Selection -->
        <validation-provider
          #default="validationContext"
          name="watchlist"
          rules="required"
        >
          <b-form-group
            :label="$tc('ntechlab.master_account.title', 2)"
            label-for="master_account"
            :state="getValidationState(validationContext)"
          >
            <v-select
              v-model="selectedMasterAccount"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="masterAccountOptions"
              :reduce="(val) => val.id"
              label="name"
              input-id="master_account"
              :clearable="false"
              :class="{ 'v-select-large': large }"
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <!-- Image Upload -->
        <b-form-group :label="$t('ntechlab.face.image')" label-for="dropzone">
          <vue-dropzone
            v-show="!previewUrl"
            id="dropzone"
            ref="dropzoneImage"
            :options="dropzoneOptions"
            class="dropzone-main"
            :class="{ 'dropzone-large': large }"
            @vdropzone-file-added="addFileImage"
            @vdropzone-removed-file="removeFileImage"
          />
          <div v-if="previewUrl" class="ntechlab-preview">
            <div class="ntechlab-preview__frame">
              <img :src="previewUrl" :alt="fileName" class="ntechlab-preview__img" />
              <div v-if="inputError" class="ntechlab-preview__overlay">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="30"
                  class="ntechlab-preview__overlay-icon"
                />
                <span class="ntechlab-preview__overlay-text">{{ inputError }}</span>
              </div>
            </div>
            <div class="ntechlab-preview__meta">
              <span class="ntechlab-preview__name" :title="fileName">{{ fileName }}</span>
              <b-button
                variant="outline-danger"
                size="sm"
                class="text-capitalize"
                @click="clearImage"
              >
                <feather-icon icon="Trash2Icon" size="14" class="mr-25" />
                {{ $t('actions.delete') }}
              </b-button>
            </div>
          </div>
        </b-form-group>

        <!-- Search Button -->
        <div :class="['d-flex justify-content-end', { 'btn-large-wrap': large }]">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="text-capitalize"
            :class="{ 'btn-large': large }"
            :disabled="!canSearch || isLoading"
            @click="searchFace"
          >
            <b-spinner v-if="isLoading" small class="mr-50"></b-spinner>
            {{ $t('ntechlab.face_search.search') }}
          </b-button>
        </div>
      </div>

      <!-- Results Table + Change Search Button -->
      <div v-else :class="['results-scrollable', { 'results-scrollable-large': large }]">
        <div class="d-flex mt-2 justify-content-end">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="secondary"
            class="text-capitalize"
            :class="{ 'btn-large': large }"
            @click="resetSearch"
          >
            {{ $t('ntechlab.face_search.change_search') || 'Change Search' }}
          </b-button>
        </div>
        <div class="d-flex justify-content-center mt-2">
          <img :src="imageBbox" alt="face-search-1" :class="imageBboxClass" @load="onImageLoad" />
        </div>
        <b-table
          :items="paginatedResults"
          :fields="tableFields"
          class="mt-2"
          :class="{ 'b-table-large': large }"
          show-empty
        >
          <!-- No found faces-->
          <template #empty>
            <div class="face-search-empty">
              <feather-icon icon="SearchIcon" size="34" />
              <p class="mb-0">
                {{ noResults || $t('ntechlab.face_search.no_results_found') }}
              </p>
            </div>
          </template>
          <template #cell(thumbnail)="data">
            <b-img
              :src="data.item.thumbnail"
              thumbnail
              fluid
              height="120"
              class="clickable-thumbnail thumbnail-large"
              @click="showFullframe(data.item.fullframe)"
            />
          </template>
          <template #cell(camera)="data">
            <div class="camera-cell">
              <span>{{ getCameraName(data.item) }}</span>
              <span
                v-if="allowMapDrag"
                v-b-tooltip.hover="mapIconTooltip(data.item)"
                class="camera-cell__map"
                :class="{ 'camera-cell__map--empty': !getCameraLocation(data.item) }"
                draggable="true"
                @dragstart="onDragMap(data.item)"
              >
                <feather-icon icon="MapIcon" size="18" />
              </span>
            </div>
          </template>
          <template #cell(metadata)="data">
            <div v-html="getFeatures(data.item)"></div>
          </template>
          <template #cell(created_date)="data">
            {{ formatDate(data.item.created_date) }}
          </template>
        </b-table>
        <b-pagination
          v-if="totalCount > perPage"
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="perPage"
          align="center"
          class="my-2"
          :class="{ 'pagination-large': large }"
        />

        <!-- Fullframe Modal -->
        <b-modal
          v-model="showFullframeModal"
          size="lg"
          hide-footer
          :title="$t('ntechlab.face_search.full_image')"
          centered
        >
          <b-img :src="selectedFullframe" fluid class="w-100" />
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormInvalidFeedback,
  BButton,
  BImg,
  BTable,
  BPagination,
  BModal,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import { ValidationProvider } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed, onMounted, onBeforeUnmount } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import useLayoutSlots from "@/composables/layoutSlots";

// Formatos y tamaño que acepta /v1/ntechlab_search_face
const ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const ACCEPTED_IMAGE_TYPES = ["image/jpg", "image/jpeg", "image/png"];
const MAX_IMAGE_SIZE_MB = 2;

export default {
  components: {
    BFormGroup,
    BFormInvalidFeedback,
    BButton,
    BImg,
    vSelect,
    vueDropzone: vue2Dropzone,
    ValidationProvider,
    BTable,
    BPagination,
    BModal,
    BSpinner,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  props: {
    large: {
      type: Boolean,
      default: false,
    },
    allowMapDrag: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18nUtils();
    const { setSlotData } = useLayoutSlots();
    const toast = useToast();
    const selectedMasterAccount = ref(null);
    const dropzoneImage = ref(null);
    const dropzoneImageFile = ref(null);
    const showForm = ref(true);
    const showFullframeModal = ref(false);
    const selectedFullframe = ref("");
    const isLoading = ref(false);
    const imageBbox = ref("");
    const imageBboxClass = ref("");
    const inputError = ref("");
    const noResults = ref("");

    // Load watchlist options
    onMounted(() => {
      store.dispatch("ntechlab_master_account/getAll");
    });

    const masterAccountOptions = computed(() =>
      store.getters["ntechlab_master_account/getSelectedItems"]
    );

    const dropzoneOptions = ref({
      url: `${window.__env.VUE_APP_BASE_URL}/v1/ntechlab_search_face`,
      paramName: "photo",
      autoProcessQueue: false,
      dictDefaultMessage: t("messages.import.select_file") + " (.png .jpeg .jpg)",
      thumbnailMethod: "crop",
      addRemoveLinks: false,
      thumbnailWidth: null,
      thumbnailHeight: null,
      dictRemoveFile: t("actions.delete"),
      acceptedFiles: ACCEPTED_IMAGE_EXTENSIONS.join(","),
      maxFilesize: MAX_IMAGE_SIZE_MB,
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")} Accept-Language: ${localStorage.getItem("language")}` },
      previewTemplate: '<div></div>',
    });

    const canSearch = computed(() => {
      return selectedMasterAccount.value && dropzoneImageFile.value;
    });

    const fileName = computed(() => dropzoneImageFile.value ? dropzoneImageFile.value.name : '');

    const previewUrl = ref("");

    const revokePreviewUrl = () => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
        previewUrl.value = "";
      }
    };

    const fileLabels = {
      invalidFormat: t("ntechlab.face_search.invalid_format"),
      maxSize: t("ntechlab.face_search.max_size"),
    };

    const validateImage = (file) => {
      const name = (file?.name || "").toLowerCase();
      const type = (file?.type || "").toLowerCase();

      const hasValidExtension = ACCEPTED_IMAGE_EXTENSIONS.some((extension) =>
        name.endsWith(extension)
      );
      const hasValidType = !type || ACCEPTED_IMAGE_TYPES.includes(type);

      if (!hasValidExtension || !hasValidType) return fileLabels.invalidFormat;

      if (file?.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) return fileLabels.maxSize;

      return "";
    };

    const addFileImage = (file) => {
      const validationError = validateImage(file);

      if (validationError) {
        if (dropzoneImage.value) {
          dropzoneImage.value.removeFile(file);
        }
        removeFileImage();
        toast({
          component: ToastificationContent,
          props: {
            title: validationError,
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      dropzoneImageFile.value = file;
      inputError.value = "";
      revokePreviewUrl();
      previewUrl.value = URL.createObjectURL(file);
    };

    const removeFileImage = () => {
      dropzoneImageFile.value = null;
      inputError.value = "";
      revokePreviewUrl();
    };

    const clearImage = () => {
      if (dropzoneImage.value) {
        dropzoneImage.value.removeAllFiles();
      }
      removeFileImage();
    };

    onBeforeUnmount(() => {
      revokePreviewUrl();
    });

    const results = ref([]);
    const totalCount = ref(0);
    const perPage = ref(10);
    const currentPage = ref(1);

    // Computed for paginated results
    const paginatedResults = computed(() => {
      const start = (currentPage.value - 1) * perPage.value;
      return results.value.slice(start, start + perPage.value);
    });

    const searchFace = async () => {
      if (!canSearch.value) return;
      try {
        isLoading.value = true;
        inputError.value = "";
        noResults.value = "";
        const formData = new FormData();
        formData.append("photo", dropzoneImageFile.value);
        formData.append("id_master_account", selectedMasterAccount.value);

        const data = await store.dispatch("ntechlab_face/searchFace", formData);
        if (data.code === 404) {
          inputError.value =
            data.error_face || t("ntechlab.face_search.no_face_detected");
          return;
        }
        results.value = data.results || [];
        totalCount.value = results.value.length;
        currentPage.value = 1;
        imageBbox.value = data.image_with_bbox;
        showForm.value = false;
        if (data?.no_results) {
          noResults.value = data.no_results;
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.message || t("Unknown error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isLoading.value = false;
      }
    };

    const { getValidationState } = formValidation();

    const tableFields = [
      { key: "thumbnail", label: "Thumbnail" },
      { key: "camera", label: "Camera" },
      { key: "metadata", label: "Metadata", thClass: 'metadata-col', tdClass: 'metadata-col' },
      { key: "created_date", label: "Date" },
    ];

    const resetSearch = () => {
      showForm.value = true;
      results.value = [];
      inputError.value = "";
      noResults.value = "";
      dropzoneImageFile.value = null;
      if (dropzoneImage.value) {
        dropzoneImage.value.removeAllFiles();
      }
      selectedMasterAccount.value = null;
    };

    const showFullframe = (url) => {
      selectedFullframe.value = url;
      showFullframeModal.value = true;
    };

    function formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const pad = n => n.toString().padStart(2, '0');
      const hh = pad(date.getHours());
      const mm = pad(date.getMinutes());
      const ss = pad(date.getSeconds());
      const yyyy = date.getFullYear();
      const MM = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      return `${hh}:${mm}:${ss} ${dd}-${MM}-${yyyy}`;
    }

    const getCameraName = (item) => {
      return (item && item.verbose_camera && item.verbose_camera.name) || 'N/A';
    };

    const mapLabels = {
      dragToMap: t("ntechlab.face_search.drag_to_map"),
      withoutLocation: t("ntechlab.face_search.camera_without_location"),
      unitsMap: t(`slot_types.${SLOT_TYPES.UNITS_MAP}`),
      customMap: t(`slot_types.${SLOT_TYPES.CUSTOM_MAP}`),
    };

    const getCameraLocation = (item) => {
      const unit = item?.omnicloud_camera?.unit;

      if (!unit) return null;

      const isUnitLoaded = (store.state.grid.units || []).some(
        (gridUnit) => String(gridUnit.id) === String(unit.id)
      );

      const lat = parseFloat(unit.coordinates?.lat);
      const lng = parseFloat(unit.coordinates?.lng);
      const hasCoordinates = !Number.isNaN(lat) && !Number.isNaN(lng);

      if (isUnitLoaded) {
        return {
          type: "unit",
          unitId: unit.id,
          name: unit.description,
          center: hasCoordinates ? { lat, lng } : null,
        };
      }

      if (!hasCoordinates) return null;

      return { type: "coordinates", lat, lng, name: unit.description };
    };

    const mapIconTooltip = (item) => {
      const location = getCameraLocation(item);

      if (!location) return mapLabels.withoutLocation;

      return `${mapLabels.dragToMap}: ${location.name}`;
    };

    const onDragMap = (item) => {
      const location = getCameraLocation(item);

      if (!location) {
        toast({
          component: ToastificationContent,
          props: {
            title: mapLabels.withoutLocation,
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      if (location.type === "unit") {
        setSlotData(
          SLOT_TYPES.UNITS_MAP,
          `${mapLabels.unitsMap}: ${location.name}`,
          {
            unit_id: location.unitId,
            // Sólo se dibuja el marcador de esta instalación
            single_unit: true,
            data: location.center ? { center: location.center, zoom: 18 } : null,
          },
          SOURCES.FACE_SEARCH
        );
        return;
      }

      setSlotData(
        SLOT_TYPES.CUSTOM_MAP,
        `${mapLabels.customMap}: ${location.name}`,
        { lat: location.lat, lng: location.lng },
        SOURCES.FACE_SEARCH
      );
    };

    const getFeatures = (item) => {
      const features = item.features;
      const hasBeard = features.beard.confidence >= 0.5;
      const age = features.age.name;
      const emotion = features.emotions.name;
      const gender = features.gender.name;
      const glasses = features.glasses.name;
      const medmask = features.medmask.name;
      const response = `
      <ul>
        <li>Beard: ${hasBeard ? 'Yes' : 'No'}</li>
        <li>Age: ${age}</li>
        <li>Emotion: ${emotion}</li>
        <li>Gender: ${gender}</li>
        <li>Glasses: ${glasses}</li>
        <li>Medical Mask: ${medmask}</li>
      </ul>`;
              return response;
      };

    const onImageLoad = (event) => {
      const img = event.target;
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      if (width > height) {
        imageBboxClass.value = 'img-bbox-width';
      } else if (height > width) {
        imageBboxClass.value = 'img-bbox-height';
      } else {
        imageBboxClass.value = 'img-bbox-square';
      }
    };

    return {
      selectedMasterAccount,
      masterAccountOptions,
      dropzoneOptions,
      dropzoneImage,
      canSearch,
      getValidationState,
      addFileImage,
      removeFileImage,
      clearImage,
      searchFace,
      fileName,
      previewUrl,
      results,
      totalCount,
      perPage,
      currentPage,
      paginatedResults,
      tableFields,
      showForm,
      resetSearch,
      showFullframeModal,
      selectedFullframe,
      showFullframe,
      formatDate,
      getCameraName,
      getCameraLocation,
      mapIconTooltip,
      onDragMap,
      isLoading,
      getFeatures,
      imageBbox,
      imageBboxClass,
      onImageLoad,
      inputError,
      noResults,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.face-detection-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.face-search-container {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.face-search-large {
  padding: 2.5rem 2rem 2rem 2rem;
  font-size: 1.25rem;
}

.results-scrollable {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  padding: 0 1rem 1rem;
}
.results-scrollable-large {
  flex: 1;
}

.face-search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  color: #b9b9c3;
  text-align: center;

  .dark-layout & {
    color: var(--third-light);
  }
}

.dropzone-main {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 150px;
  margin: auto;
  padding: 0.5rem;
  border: 1.5px dashed #d8d6de;
  border-radius: 0.428rem;
  background-position: center;
  background-size: cover;

  .dark-layout & {
    border-color: $theme-dark-border-color;
  }
}
.dropzone-large {
  min-height: 220px;
}

.ntechlab-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ntechlab-preview__frame {
  position: relative;
  align-self: center;
  max-width: 100%;
  line-height: 0;
}

.ntechlab-preview__img {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 260px;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  object-fit: contain;

  .dark-layout & {
    border-color: $theme-dark-border-color;
  }
}

.ntechlab-preview__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 0.428rem;
}

.ntechlab-preview__overlay-icon {
  color: #ff9f43;
}

.ntechlab-preview__overlay-text {
  color: #fff;
  font-size: 0.9rem;
  line-height: 1.35;
}

.ntechlab-preview__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.ntechlab-preview__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
  color: #6e6b7b;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.btn-large {
  font-size: 1.25rem !important;
  padding: 0.75rem 2.5rem !important;
}

.b-table-large {
  font-size: 1.15rem;
}

/* Ensure table doesn't create its own scroll */
.table-responsive {
  overflow: visible !important;
}

.results-scrollable .table {
  margin-bottom: 0;
}

.thumbnail-large {
  height: 150px !important;
  max-width: 150px !important;
}

.camera-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.camera-cell__map {
  display: inline-flex;
  flex: 0 0 auto;
  cursor: grab;
  color: var(--primary);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.15);
  }

  &:active {
    cursor: grabbing;
  }
}

.camera-cell__map--empty {
  color: #b9b9c3;
}

.clickable-thumbnail {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.clickable-thumbnail:hover {
  transform: scale(1.05);
}

.metadata-scrollable {
  max-height: 240px;
  max-width: 420px;
  overflow: auto;
  background: #848f9c;
  color: #fff;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.85rem;

  pre {
    margin: 0;
    white-space: pre;
    word-break: break-all;
  }
}
.metadata-scrollable-large {
  max-height: 350px;
  font-size: 1.1rem;
}

.metadata-col {
  min-width: 210px;
  width: 210px;
  max-width: 210px;
}
.img-bbox-width {
  max-width: 200px;
}
.img-bbox-height {
  max-height: 200px;
}
.img-bbox-square {
  max-width: 200px;
  max-height: 200px;
}

/* Form section - scrolls on its own when the slot is short */
.form-section {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 1rem 1rem;
}

/* Results controls - fixed size */
.results-scrollable > .d-flex:first-child,
.results-scrollable > .d-flex:last-child {
  flex-shrink: 0;
}


</style>
